import { useRef, useContext, useEffect } from "react";
import log from "loglevel";
import newid from "../utils/new-id";
import {
  removePointFromPath,
  insertPointIntoPath,
  BoundBox,
  checkifPolygonLinesIntersect,
} from "../utils/mapping-functions";
import SitePlanContext from "../utils/siteplan-context";

import AutoHeightEmbed from "../components/auto-height-embed";
import Map, { Marker, Polygon, GoogleApiWrapper } from "google-maps-react";
import DebouncedSearch from "./debounced-search";

import { FA } from "../utils/font-awesome";

let listeners = [];

const MapDrawBoundary = (props) => {
  const sitePlanContext = useContext(SitePlanContext);
  const siteBounds = BoundBox.createFromMarkers(sitePlanContext.sitePlanCoords);
  const mapRef = useRef(null);
  const siteMapRef = useRef(null);

  // listen to changes made directly with google’s edit option and save them

  const saveAllCoordsFromGoogle = () => {
    console.log(siteMapRef.current.polygon.getPath().getArray());
    const siteCoords = siteMapRef.current.polygon
      .getPath()
      .getArray()
      .map(({ lat, lng }) => {
        return { lat: lat(), lng: lng() };
      });
    sitePlanContext.updateSitePlanCoords(siteCoords);
  };

  const addListeners = () => {
    listeners.forEach((item, i) => {
      props.google.maps.event.removeListener(item);
    });
    listeners = [];
    if (siteMapRef.current) {
      listeners.push(
        props.google.maps.event.addListener(
          siteMapRef.current.polygon.getPath(),
          "insert_at",
          saveAllCoordsFromGoogle
        )
      );
      listeners.push(
        props.google.maps.event.addListener(
          siteMapRef.current.polygon.getPath(),
          "set_at",
          saveAllCoordsFromGoogle
        )
      );
    }
  };

  useEffect(() => {
    addListeners();
    if (siteMapRef.current) {
      // export useful calculated data
      const siteLength = props.google.maps.geometry.spherical.computeLength(
        siteMapRef.current.polygon.getPath()
      );
      const siteArea = props.google.maps.geometry.spherical.computeArea(
        siteMapRef.current.polygon.getPath()
      );
      props.onUpdate({ area: siteArea, length: siteLength });
    }
  }, [sitePlanContext.sitePlanCoords]);

  // click listeners

  const addPolygonMarker = (mapProps, map, clickEvent) => {
    const clickEventLatlngObject = {
      lat: clickEvent.latLng.lat(),
      lng: clickEvent.latLng.lng(),
    };

    const updatedCoords = insertPointIntoPath(
      [...sitePlanContext.sitePlanCoords],
      clickEventLatlngObject
    );
    if (checkifPolygonLinesIntersect(updatedCoords)) {
      log.info("it interesects");
    }
    sitePlanContext.updateSitePlanCoords(updatedCoords);
  };

  const removeMarker = (mapProps, map, clickEvent) => {
    const clickEventLatlngObject = {
      lat: clickEvent.latLng.lat(),
      lng: clickEvent.latLng.lng(),
    };

    const updatedCoords = removePointFromPath(
      [...sitePlanContext.sitePlanCoords],
      clickEventLatlngObject
    );
    // Does nothing if you didn’t click a vertex
    sitePlanContext.updateSitePlanCoords(updatedCoords);
  };

  // control to recenter the map

  const recenterMap = (fitBounds) => {
    mapRef.current.map.fitBounds(fitBounds);
  };
  useEffect(
    () => recenterMap(siteBounds.literal),
    [sitePlanContext.allSites.current]
  );

  const centerRef = useRef();
  const centerButton = (
    <span
      className="google-maps-button"
      ref={centerRef}
      onClick={() => recenterMap(sitePlanContext.boundBox.literal)}
    >
      <FA icon="map-marked-alt" />
    </span>
  );
  const centerSiteRef = useRef();
  const centerSiteButton = (
    <span
      className="google-maps-button"
      ref={centerSiteRef}
      onClick={() => recenterMap(siteBounds.literal)}
    >
      <FA icon="vector-square" />
    </span>
  );

  const changeSearchText = async (txt) => {
    if (txt) {
      log.info(txt);
      var searchBox = await new google.maps.places.SearchBox(txt);
      searchBox.setBounds(sitePlanContext.boundBox.literal);
      log.info(searchBox);
      var places = await searchBox.getPlaces();
      log.info(txt, places);
      if (places.length == 0) {
      }
      places.forEach(function (place) {
        if (!place.geometry) {
          console.log("Returned place contains no geometry");
          return;
        } else {
          log.info(place.geometry.location);
        }
      });
      recenterMap(sitePlanContext.boundBox.literal);
    }
  };

  const searchRef = useRef();
  const searchBar = (
    <span className="google-maps-search" ref={searchRef}>
      <DebouncedSearch placeholder="Search maps" onChange={changeSearchText} />
    </span>
  );

  //

  const mapIsReady = () => {
    addListeners();
    mapRef.current.map.controls[
      props.google.maps.ControlPosition.TOP_RIGHT
    ].push(centerRef.current);
    mapRef.current.map.controls[
      props.google.maps.ControlPosition.TOP_RIGHT
    ].push(centerSiteRef.current);
    mapRef.current.map.controls[
      props.google.maps.ControlPosition.TOP_LEFT
    ].push(searchRef.current);
  };

  return (
    <AutoHeightEmbed>
      <Map
        google={props.google}
        onReady={mapIsReady}
        onClick={addPolygonMarker}
        scaleControl={true}
        streetViewControl={false}
        fullscreenControl={false}
        ref={mapRef}
      >
        {props.showMarkers &&
          sitePlanContext.poiMarkers.map((coords, i) => {
            return (
              <Marker
                name={i}
                label={coords.name}
                position={coords.latLng}
                key={i}
                clickable={false}
              />
            );
          })}
        {sitePlanContext.sitePlanCoords.length && (
          <Polygon
            ref={siteMapRef}
            paths={sitePlanContext.sitePlanCoords}
            editable={true}
            onClick={removeMarker}
            strokeColor="#0000FF"
            strokeOpacity={0.8}
            strokeWeight={2}
            fillColor={
              checkifPolygonLinesIntersect(sitePlanContext.sitePlanCoords)
                ? "#FF0000"
                : "#0000FF"
            }
            fillOpacity={0.35}
          />
        )}
      </Map>
      {centerButton}
      {centerSiteButton}
      {/* {searchBar} */}
    </AutoHeightEmbed>
  );
};

export default GoogleApiWrapper({
  apiKey: "AIzaSyAraKWlMHCcvmJMIfo8fEcUNe6IEzv9VWs",
  libraries: ["geometry", "places"],
})(MapDrawBoundary);
